import { useState } from 'react';
import { Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import {
  BasicModal,
  Button,
  CustomIcon,
  FormModal,
  RestrictedComponent
} from '~components/index';
import { INPUT_REQUIRED, PERMISSIONS } from '~constants/index';
import { useLocalStorage } from '~hooks/index';
import { logout } from '~layouts/ErrorBoundaries/ErrorBoundaryBack/ErrorBoundaryBackRequests';
import { ROUTES } from '~routes/index';
import { sidebarItemsRender } from './SidebarHandler';
import { createCode, updateUserStorage } from './SidebarRequests';
import './sidebar.scss';

const defaultLanguage = import.meta.env.VITE_DEFAULT_LANGUAGE;

interface Props {
  collapse: boolean;
}
const Sidebar = ({ collapse }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { appName, userData, appImplementation, userPermissions, setLanguage } =
    useLocalStorage();
  const [showModal, setShowModal] = useState('none');
  const [code, setCode] = useState<string | null>(null);
  const canViewOperations = userPermissions.includes(
    PERMISSIONS.PATIENT_MANAGEMENT.CAN_VIEW_OPERATION
  );
  const clearAppStorage = () => {
    localStorage.removeItem(appName);
    window.location.replace('/');
    setLanguage(defaultLanguage);
  };

  const availableModals: Record<string, JSX.Element> = {
    deployModal: (
      <BasicModal
        title="btns.releases"
        body={
          <>
            <div className="d-flex flex-column gap-2">
              <ul>
                <li>202 loads</li>
                <li>Agrupador de tabs</li>
                <li>Etiquetas FCI</li>
                <li>100 registros por defecto en torre de control</li>
              </ul>
            </div>
            <div className="d-flex flex-column gap-2">
              <p>
                <b>Version:</b> 2.2.0
              </p>
              <p>
                <b>Rama:</b> git checkout feature/RF-220 2025-10-24
              </p>
            </div>
          </>
        }
        onHide={() => setShowModal('none')}
      />
    ),
    generateCode: (
      <FormModal
        title="btns.generate_code"
        inputs={() => [
          {
            id: 'document_number_id',
            type: 'text',
            name: 'document_number',
            label: 'labels.document_number',
            placeholder: 'placeholders.document_number',
            rules: INPUT_REQUIRED
          }
        ]}
        submitFn={(data) => {
          const { document_number } = data as { document_number: string };
          if (document_number) createCode({ document_number, setCode });
        }}
        extraBody={
          code && (
            <div className="d-flex gap-1">
              <p className="fw-bold">{t('labels.code_generated')}</p>
              {code}
            </div>
          )
        }
        onHide={() => {
          setShowModal('none');
          setCode(null);
        }}
      />
    )
  };

  const viewUserProfile = userPermissions.includes(
    PERMISSIONS.USERS.CAN_VIEW_USER_PROFILE
  );

  return (
    <>
      {showModal !== 'none' && availableModals[showModal]}
      <div
        className={`sidebar ${collapse ? 'sidebar_hidden' : 'sidebar_visible'}`}
      >
        <div className="sidebar_content">
          <NavLink to={ROUTES.PLATFORM} className="sidebar_logo_container">
            <Image
              src={appImplementation?.logos?.logo_inverse}
              alt="app_logo"
              className="sidebar_logo"
            />
          </NavLink>
          <p className="sidebar_tenant_name mb-2">
            {userData?.tenantSelected?.name}
          </p>
          {userData.is_superuser && (
            <Button
              variant="link"
              iconName="releases"
              title="btns.releases"
              onClick={() => setShowModal('deployModal')}
            />
          )}
          {sidebarItemsRender(userData, location, canViewOperations)}
          <RestrictedComponent
            permissions={[PERMISSIONS.NAV_BAR.CAN_VIEW_GENERATE_CODE]}
          >
            <Button
              variant="link"
              text="btns.generate_code"
              iconName="outlineSecurity"
              onClick={() => setShowModal('generateCode')}
            />
          </RestrictedComponent>
          <div className="sidebar_userOptions">
            <div
              className={viewUserProfile ? 'sidebar_userProfile' : ''}
              title={t('btns.user_profile')}
              onClick={
                viewUserProfile
                  ? () => navigate(ROUTES.USER_PROFILE)
                  : undefined
              }
            >
              <CustomIcon
                name="user"
                color="var(--sidebar-icon-color)"
                size="LG"
                className={viewUserProfile ? 'cursor-pointer' : ''}
              />
            </div>

            <div className="sidebar_userLogged">
              <div className="d-flex gap-2 align-items-center">
                {userData?.first_name ?? ' - '}
                <div title={t('btns.update_user')} onClick={updateUserStorage}>
                  <CustomIcon
                    name="reload"
                    color="var(--sidebar-text-color)"
                    size="XS"
                    className="cursor-pointer"
                  />
                </div>
              </div>
              <Button
                variant="link"
                iconName="logout"
                text="btns.logout"
                onClick={() =>
                  logout(userData?.tokens?.refresh, clearAppStorage)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
